import React, { useState } from 'react';
import { SettingsCard } from './shared/SettingsCard';
import { SettingsToggle } from './shared/SettingsToggle';
import { Type, Layout, Film, Sparkles } from 'lucide-react';
interface DisplaySectionProps {
  setHasUnsavedChanges: (hasChanges: boolean) => void;
}
export const DisplaySection: React.FC<DisplaySectionProps> = ({
  setHasUnsavedChanges
}) => {
  const [display, setDisplay] = useState({
    fontSize: 'default',
    uiDensity: 'standard',
    animations: true,
    autoplayVideos: false
  });
  const handleChange = (field: string, value: string | boolean) => {
    setDisplay(prev => ({
      ...prev,
      [field]: value
    }));
    setHasUnsavedChanges(true);
  };
  return <div className="divide-y divide-gray-200 dark:divide-gray-800" data-id="element-18578">
      <SettingsCard title="Text & Layout" description="Adjust the appearance of text and UI elements." data-id="element-18579">
        <div className="space-y-6" data-id="element-18580">
          {/* Font Size */}
          <div data-id="element-18581">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-18582">
              Font Size
            </label>
            <div className="flex items-center gap-3" data-id="element-18583">
              <div className="flex-1 flex items-center gap-2" data-id="element-18584">
                <Type size={16} className="text-gray-500" data-id="element-18585" />
                <div className="w-full bg-gray-200 dark:bg-gray-700 h-2 rounded-full" data-id="element-18586">
                  <div className={`h-full rounded-full bg-gradient-to-r from-pink-500 to-purple-500 relative ${display.fontSize === 'small' ? 'w-1/3' : display.fontSize === 'default' ? 'w-2/3' : 'w-full'}`} data-id="element-18587">
                    <div className="absolute -right-2 -top-1.5 w-5 h-5 bg-white dark:bg-gray-900 rounded-full border-2 border-pink-500 dark:border-purple-500" data-id="element-18588"></div>
                  </div>
                </div>
              </div>
              <span className="text-sm text-gray-700 dark:text-gray-300 w-16 text-right" data-id="element-18589">
                {display.fontSize === 'small' ? 'Small' : display.fontSize === 'default' ? 'Default' : 'Large'}
              </span>
            </div>
            <div className="flex justify-between mt-3" data-id="element-18590">
              <button className={`px-3 py-1.5 rounded text-xs font-medium ${display.fontSize === 'small' ? 'bg-gradient-to-r from-pink-600 to-purple-600 text-white' : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'}`} onClick={() => handleChange('fontSize', 'small')} data-id="element-18591">
                Small
              </button>
              <button className={`px-3 py-1.5 rounded text-xs font-medium ${display.fontSize === 'default' ? 'bg-gradient-to-r from-pink-600 to-purple-600 text-white' : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'}`} onClick={() => handleChange('fontSize', 'default')} data-id="element-18592">
                Default
              </button>
              <button className={`px-3 py-1.5 rounded text-xs font-medium ${display.fontSize === 'large' ? 'bg-gradient-to-r from-pink-600 to-purple-600 text-white' : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'}`} onClick={() => handleChange('fontSize', 'large')} data-id="element-18593">
                Large
              </button>
            </div>
          </div>
          {/* UI Density */}
          <div data-id="element-18594">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-18595">
              UI Density
            </label>
            <div className="grid grid-cols-3 gap-3" data-id="element-18596">
              <button className={`p-3 rounded-lg border-2 transition-colors ${display.uiDensity === 'compact' ? 'border-pink-500 dark:border-purple-500 bg-pink-50 dark:bg-purple-900/20' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'}`} onClick={() => handleChange('uiDensity', 'compact')} data-id="element-18597">
                <div className="flex flex-col items-center" data-id="element-18598">
                  <Layout size={20} className={display.uiDensity === 'compact' ? 'text-pink-500 dark:text-purple-400' : 'text-gray-500 dark:text-gray-400'} data-id="element-18599" />
                  <span className="mt-2 text-xs font-medium text-gray-700 dark:text-gray-300" data-id="element-18600">
                    Compact
                  </span>
                  <div className="mt-2 w-full space-y-1" data-id="element-18601">
                    <div className="h-1 bg-gray-200 dark:bg-gray-700 rounded-full" data-id="element-18602"></div>
                    <div className="h-1 bg-gray-200 dark:bg-gray-700 rounded-full" data-id="element-18603"></div>
                    <div className="h-1 bg-gray-200 dark:bg-gray-700 rounded-full" data-id="element-18604"></div>
                  </div>
                </div>
              </button>
              <button className={`p-3 rounded-lg border-2 transition-colors ${display.uiDensity === 'standard' ? 'border-pink-500 dark:border-purple-500 bg-pink-50 dark:bg-purple-900/20' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'}`} onClick={() => handleChange('uiDensity', 'standard')} data-id="element-18605">
                <div className="flex flex-col items-center" data-id="element-18606">
                  <Layout size={20} className={display.uiDensity === 'standard' ? 'text-pink-500 dark:text-purple-400' : 'text-gray-500 dark:text-gray-400'} data-id="element-18607" />
                  <span className="mt-2 text-xs font-medium text-gray-700 dark:text-gray-300" data-id="element-18608">
                    Standard
                  </span>
                  <div className="mt-2 w-full space-y-1.5" data-id="element-18609">
                    <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full" data-id="element-18610"></div>
                    <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full" data-id="element-18611"></div>
                    <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full" data-id="element-18612"></div>
                  </div>
                </div>
              </button>
              <button className={`p-3 rounded-lg border-2 transition-colors ${display.uiDensity === 'spacious' ? 'border-pink-500 dark:border-purple-500 bg-pink-50 dark:bg-purple-900/20' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'}`} onClick={() => handleChange('uiDensity', 'spacious')} data-id="element-18613">
                <div className="flex flex-col items-center" data-id="element-18614">
                  <Layout size={20} className={display.uiDensity === 'spacious' ? 'text-pink-500 dark:text-purple-400' : 'text-gray-500 dark:text-gray-400'} data-id="element-18615" />
                  <span className="mt-2 text-xs font-medium text-gray-700 dark:text-gray-300" data-id="element-18616">
                    Spacious
                  </span>
                  <div className="mt-2 w-full space-y-2" data-id="element-18617">
                    <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full" data-id="element-18618"></div>
                    <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full" data-id="element-18619"></div>
                    <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full" data-id="element-18620"></div>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </SettingsCard>
      <SettingsCard title="Visual Effects" description="Control animations and visual effects." data-id="element-18621">
        <div className="space-y-4" data-id="element-18622">
          <SettingsToggle id="animations" label="Animations" description="Enable animations and transitions throughout the interface." checked={display.animations} onChange={checked => handleChange('animations', checked)} tooltip="Disabling animations may improve performance on older devices." data-id="element-18623" />
          <SettingsToggle id="autoplay-videos" label="Autoplay Video Previews" description="Automatically play videos when scrolling through your feed." checked={display.autoplayVideos} onChange={checked => handleChange('autoplayVideos', checked)} tooltip="Videos will play automatically when they come into view." data-id="element-18624" />
          <div className="p-4 bg-gradient-to-r from-pink-500/10 to-purple-500/10 dark:from-pink-900/20 dark:to-purple-900/20 rounded-lg border border-pink-200/20 dark:border-pink-800/30 mt-2" data-id="element-18625">
            <div className="flex items-center gap-3" data-id="element-18626">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-pink-100 dark:bg-pink-900/30" data-id="element-18627">
                <Sparkles size={20} className="text-pink-500" data-id="element-18628" />
              </div>
              <div data-id="element-18629">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-18630">
                  Animations Preview
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-18631">
                  {display.animations ? 'Animations are currently enabled. UI elements will animate smoothly.' : 'Animations are currently disabled. UI elements will change instantly.'}
                </p>
              </div>
            </div>
            {display.animations && <div className="mt-4 flex justify-center" data-id="element-18632">
                <div className="w-16 h-16 bg-gradient-to-r from-pink-500 to-purple-500 rounded-lg animate-pulse" data-id="element-18633"></div>
              </div>}
          </div>
        </div>
      </SettingsCard>
    </div>;
};